import cn from 'classnames';

import { BRAND_SPECIFIC_TAG } from 'components/common/AppliedOrOfferTag';
import DiscountDetailsModal from 'components/common/modals/DiscountDetailsModal.js';
import ClosedBox from 'components/icons/illustrative/ClosedBox';
import OpenBox from 'components/icons/OpenBox';
import { TEMPO } from 'context/BrandContext';
import useBrand from 'hooks/useBrand';
import { ICON_SIZES } from 'lib/constants/iconSizes';

const SingleDiscountGraphic = ({ discount, orderDiscounts, idx }) => {
  const isTempo = useBrand(TEMPO);
  const { processed, isCurrentDiscount, displayValue } = discount;
  const isCurrentElementNotLast = idx !== orderDiscounts.length - 1;
  const areCurrentAndNextDiscountsProcessed = isCurrentElementNotLast
    ? processed && orderDiscounts[idx + 1].processed
    : false;
  let ariaLabel;
  if (isCurrentDiscount) {
    ariaLabel = `order discount ${idx + 1} of ${orderDiscounts.length}, ${displayValue}. applies to this week's basket`;
  } else if (processed) {
    ariaLabel = `order discount ${idx + 1} of ${orderDiscounts.length}, ${displayValue}. has been redeemed`;
  } else {
    ariaLabel = `order discount ${idx + 1} of ${orderDiscounts.length}, ${displayValue}. applies to a future basket`;
  }

  return (
    <li className="relative flex w-fit" aria-label={ariaLabel} aria-current={isCurrentDiscount}>
      <div className="z-one flex items-center gap-2">
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full border-[1.5px] border-solid',
            {
              'border-charcoal-60 bg-charcoal-50': processed,
              'border-green-110 bg-green-50': !processed && !isTempo,
              'border-tempo-blue-90 bg-tempo-blue-50': !processed && isTempo,
            }
          )}
        >
          <div
            className={cn('absolute top-[-3px] z-hidden h-full w-[2px] rounded', {
              'bg-charcoal-60': processed,
              'bg-green-110': !processed && !isTempo,
              'bg-tempo-blue-90': !processed && isTempo,
            })}
          />
          {processed ? (
            <OpenBox fill="#f5f5f6" fillStroke="#666c73" />
          ) : (
            <ClosedBox svgSize={ICON_SIZES.SM} />
          )}

          <div
            className={cn('absolute bottom-[-3px] z-hidden h-full w-[2px] rounded', {
              'bg-charcoal-60': areCurrentAndNextDiscountsProcessed,
              'bg-green-110': !areCurrentAndNextDiscountsProcessed && !isTempo,
              'bg-tempo-blue-90': !areCurrentAndNextDiscountsProcessed && isTempo,
            })}
          />
        </div>
        <span
          className={cn('textMd', {
            'text-charcoal-80 line-through decoration-[0.5px]': processed,
            'text-green-110': isCurrentDiscount && !isTempo,
            'text-tempo-blue-90': isCurrentDiscount && isTempo,
            'text-charcoal-100': !processed && !isCurrentDiscount,
          })}
        >
          {displayValue}
        </span>
      </div>
      {isCurrentDiscount && (
        <div
          className={cn(
            'absolute left-2 h-full w-full rounded-bl-lg rounded-br-sm rounded-tl-lg rounded-tr-sm',
            isTempo ? 'bg-tempo-blue-50' : 'bg-green-50'
          )}
        />
      )}
    </li>
  );
};

SingleDiscountGraphic.propTypes = {
  discount: PropTypes.object.isRequired,
  orderDiscounts: PropTypes.array.isRequired,
  idx: PropTypes.number.isRequired,
};

const VoucherDetailsModal = ({
  basketVoucherDetails,
  deliveryDate,
  displayAppliedTag,
  shown,
  upcomingOrderUrlPath,
  viewOrderAnalyticsEvent,
  onCloseVoucherDetailsModal,
}) => {
  const isTempo = useBrand(TEMPO);
  const { heading, subheading, disclaimerMessage, orderDiscounts } = basketVoucherDetails;

  const discountsGraphic =
    orderDiscounts.length > 0 ? (
      <ul className="flex flex-col gap-2 overflow-hidden">
        {orderDiscounts.map((discount, idx) => (
          <SingleDiscountGraphic
            key={idx}
            orderDiscounts={orderDiscounts}
            idx={idx}
            discount={discount}
          />
        ))}
      </ul>
    ) : null;

  return (
    <DiscountDetailsModal
      appliedOrOfferTagProps={
        displayAppliedTag
          ? {
              isApplied: true,
              ...(isTempo && { variation: BRAND_SPECIFIC_TAG }),
            }
          : null
      }
      disclaimer={disclaimerMessage}
      discountsGraphic={discountsGraphic}
      heading={heading}
      onCloseModal={onCloseVoucherDetailsModal}
      shown={shown}
      subheading={subheading}
      upcomingOrderDeliveryDate={deliveryDate}
      upcomingOrderUrlPath={upcomingOrderUrlPath}
      viewOrderAnalyticsEvent={viewOrderAnalyticsEvent}
    />
  );
};

VoucherDetailsModal.propTypes = {
  basketVoucherDetails: PropTypes.object.isRequired,
  deliveryDate: PropTypes.string.isRequired,
  displayAppliedTag: PropTypes.bool.isRequired,
  shown: PropTypes.bool.isRequired,
  upcomingOrderUrlPath: PropTypes.string.isRequired,
  viewOrderAnalyticsEvent: PropTypes.string,
  onCloseVoucherDetailsModal: PropTypes.func.isRequired,
};

export default VoucherDetailsModal;
